import React from "react";
import { SelectButton } from "primereact/selectbutton";
import { InputSwitch } from "primereact/inputswitch";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import type { ReportingOptions } from "../../../types/stationPerformance";
import {
  MONEY_VIEW_OPTIONS,
  REPORTING_UNIT_OPTIONS,
  SEGMENT_OPTIONS,
} from "../StationPerformance.consts";

interface ReportingOptionsBarProps {
  options: ReportingOptions;
  onChange: (next: ReportingOptions) => void;
  /** Variance views cannot honour a provider filter, so the input is disabled there (FR-030b). */
  providerFilterDisabled?: boolean;
  /**
   * The dashboard shows the total taken and cocharge's share as two separate cards, so it always reads
   * on the TOTAL basis. Disabled rather than ignored: a lens that silently does nothing is worse than
   * one that says it does not apply.
   */
  moneyViewDisabled?: boolean;
  /** Also called on Reset, so the page can put the month back to the current one (BOF-82). */
  onReset?: () => void;
}

/**
 * The four lenses every measure is read through, in one place (FR-030a).
 *
 * These persist across tabs on purpose: a comparison begun per connector must not silently continue per
 * site when the reader switches screen.
 */
const ReportingOptionsBar: React.FC<ReportingOptionsBarProps> = ({
  options,
  onChange,
  providerFilterDisabled = false,
  moneyViewDisabled = false,
  onReset,
}) => {
  const set = (patch: Partial<ReportingOptions>) => onChange({ ...options, ...patch });

  return (
    <div className="surface-card border-round p-3 mb-3">
      <div className="flex flex-wrap gap-4 align-items-end">
        <div className="flex flex-column gap-1">
          <label className="text-sm text-color-secondary">Reporting unit</label>
          <SelectButton
            value={options.unit}
            options={REPORTING_UNIT_OPTIONS}
            onChange={(e) => e.value && set({ unit: e.value })}
            allowEmpty={false}
          />
        </div>

        <div className="flex flex-column gap-1">
          <label className="text-sm text-color-secondary">Sessions</label>
          <SelectButton
            value={options.segment}
            options={SEGMENT_OPTIONS}
            onChange={(e) => e.value && set({ segment: e.value })}
            allowEmpty={false}
          />
        </div>

        <div className="flex flex-column gap-1">
          <label className="text-sm text-color-secondary">
            Money {moneyViewDisabled && <span className="text-orange-600">(n/a here)</span>}
          </label>
          <SelectButton
            value={options.moneyView}
            options={MONEY_VIEW_OPTIONS}
            onChange={(e) => e.value && set({ moneyView: e.value })}
            allowEmpty={false}
            disabled={moneyViewDisabled}
          />
        </div>

        <div className="flex flex-column gap-1">
          <label
            className="text-sm text-color-secondary"
            title="Free-charge sessions are included by default. Excluding them narrows every figure to paid sessions."
          >
            Include free charging
          </label>
          <InputSwitch
            checked={options.includeFreeCharge}
            onChange={(e) => set({ includeFreeCharge: !!e.value })}
          />
        </div>

        <div className="flex flex-column gap-1">
          <label className="text-sm text-color-secondary">Station</label>
          <InputText
            value={options.station ?? ""}
            placeholder="All stations"
            onChange={(e) => set({ station: e.target.value || null })}
          />
        </div>

        <div className="flex flex-column gap-1">
          <label
            className="text-sm text-color-secondary"
            title={
              providerFilterDisabled
                ? "Variance is not channel-scoped: the stored breakdown has counters only, so a per-channel spread cannot be derived"
                : "Filter to one e-mobility provider"
            }
          >
            Channel {providerFilterDisabled && <span className="text-orange-600">(n/a here)</span>}
          </label>
          <InputText
            value={options.provider ?? ""}
            placeholder="All channels"
            disabled={providerFilterDisabled}
            onChange={(e) => set({ provider: e.target.value || null })}
          />
        </div>

        <Button
          label="Reset"
          icon="pi pi-undo"
          outlined
          size="small"
          onClick={() => {
            onChange({
              unit: "SITE",
              segment: "ALL",
              moneyView: "TOTAL",
              includeFreeCharge: true,
              station: null,
              provider: null,
            });
            onReset?.();
          }}
        />
      </div>
    </div>
  );
};

export default ReportingOptionsBar;
